import { AnimatePresence, motion } from 'framer-motion';
import { MapPin, Target, TrendingUp, Users } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { h4, p, sh2, sh3, textStyle } from '../../styles/typography';
import BarChart from '../ui/BarChart';
import Card from '../ui/card';
import DonutChart from '../ui/DonutChart';
import './HyperLocalReferralTracker.css';
import ReferralDetails from './ReferralDetails';
import { getDashboardData, getInfluencerDetails } from './referralTrackerApi';

const InfluencerReferralDashboard = ({ onSelectInfluencer: onSelectInfluencerProp }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dashboardData = await getDashboardData();
      setData(dashboardData);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleSelectInfluencer = useCallback(async (item) => {
    if (onSelectInfluencerProp) {
      onSelectInfluencerProp(item);
      return;
    }
    setSelectedInfluencer(null);
    setDetailsLoading(true);
    try {
      const details = await getInfluencerDetails(item.influencer_id);
      setSelectedInfluencer(details || item);
    } catch (err) {
      console.error('Error loading influencer details:', err);
      setSelectedInfluencer(item);
    } finally {
      setDetailsLoading(false);
    }
  }, [onSelectInfluencerProp]);

  const handleCloseDetails = useCallback(() => {
    setSelectedInfluencer(null);
  }, []);

  const showOverlay = selectedInfluencer !== null;

  useEffect(() => {
    if (!showOverlay) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') handleCloseDetails();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [showOverlay, handleCloseDetails]);

  if (loading) {
    return (
      <div className="referral-dashboard referral-dashboard--loading">
        <div className="referral-tracker-spinner" />
        <p className={textStyle(p, 'text-gray-600 mt-4')}>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="referral-dashboard">
        <Card variant="light" className="referral-dashboard-error p-6">
          <p className={textStyle(p, 'text-red-600')}>{error}</p>
          <button
            type="button"
            onClick={loadDashboard}
            className="referral-dashboard-retry mt-3 px-4 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700"
          >
            Retry
          </button>
        </Card>
      </div>
    );
  }

  const { stats, topInfluencers, referralsByRegion } = data;

  return (
    <div className="referral-dashboard">
      {/* KPI row */}
      <div className="referral-dashboard-kpis">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <Card.NoShadowGradient className="referral-dashboard-kpi-card">
            <div className="referral-dashboard-kpi-row1">
              <Users className="referral-dashboard-kpi-icon" />
              <span className={textStyle(sh2, 'referral-dashboard-kpi-value')}>
                {stats.activeInfluencers}
              </span>
            </div>
            <span className={textStyle(p, 'referral-dashboard-kpi-label')}>Active influencers</span>
          </Card.NoShadowGradient>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.05 }}
        >
          <Card.NoShadowGradient className="referral-dashboard-kpi-card">
            <div className="referral-dashboard-kpi-row1">
              <TrendingUp className="referral-dashboard-kpi-icon" />
              <span className={textStyle(sh2, 'referral-dashboard-kpi-value')}>
                {stats.totalReferrals}
              </span>
            </div>
            <span className={textStyle(p, 'referral-dashboard-kpi-label')}>Total referrals</span>
          </Card.NoShadowGradient>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.1 }}
        >
          <Card.NoShadowGradient className="referral-dashboard-kpi-card">
            <div className="referral-dashboard-kpi-row1">
              <Target className="referral-dashboard-kpi-icon" />
              <span className={textStyle(sh2, 'referral-dashboard-kpi-value')}>
                {stats.totalConversions}
              </span>
            </div>
            <span className={textStyle(p, 'referral-dashboard-kpi-label')}>Conversions</span>
          </Card.NoShadowGradient>
        </motion.div>
      </div>

      {/* Charts + Top influencers row */}
      <div className="referral-dashboard-grid">
        <motion.div
          className="referral-dashboard-chart-wrap"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.15 }}
        >
          <Card.NoShadowGradient className="referral-dashboard-chart-card">
            <BarChart
              title="Referrals by Site"
              data={referralsByRegion}
              labels={referralsByRegion.map((r) => r.region)}
              yAxisLabel="Referrals"
              showExpandIcon={false}
              barColor="#0d9488"
              className="referral-dashboard-barchart"
            />
          </Card.NoShadowGradient>
        </motion.div>

        <motion.div
          className="referral-dashboard-conversion-wrap"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.2 }}
        >
          <Card.NoShadowGradient className="referral-dashboard-conversion-card">
            <h4 className={textStyle(sh2, 'referral-dashboard-conversion-title')}>
              Conversion rate
            </h4>
            <DonutChart
              percentage={stats.conversionRate ?? 0}
              size={140}
              strokeWidth={14}
              showPercentage={true}
              className="referral-dashboard-donut"
            />
          </Card.NoShadowGradient>
        </motion.div>
      </div>

      {/* Top influencers */}
      <motion.div
        className="referral-dashboard-top"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.25 }}
      >
        <Card.NoShadowGradient className="referral-dashboard-top-card">
          <h4 className={textStyle(h4, 'referral-dashboard-top-title')}>Top influencers</h4>
          <ul className="referral-dashboard-top-list">
            {topInfluencers.length === 0 ? (
              <li className={textStyle(p, 'text-gray-500')}>No data yet.</li>
            ) : (
              topInfluencers.map((inf, index) => (
                <li
                  key={inf.influencer_id}
                  className="referral-dashboard-top-item referral-dashboard-top-item--clickable"
                  role="button"
                  onClick={() => handleSelectInfluencer(inf)}
                  onKeyDown={(e) =>
                    (e.key === 'Enter' || e.key === ' ') && handleSelectInfluencer(inf)
                  }
                  tabIndex={0}
                >
                  <span className="referral-dashboard-top-rank">{index + 1}</span>
                  <div className="referral-dashboard-top-info">
                    <span className={textStyle(sh3, 'referral-dashboard-top-name')}>
                      {inf.name || '—'}
                    </span>
                    <span className={textStyle(p, 'referral-dashboard-top-meta text-gray-600')}>
                      <MapPin className="referral-dashboard-top-meta-icon" />
                      {inf.location || inf.region || '—'}
                    </span>
                  </div>
                  <span className={textStyle(sh2, 'referral-dashboard-top-count')}>
                    {inf.referral_count}
                  </span>
                </li>
              ))
            )}
          </ul>
        </Card.NoShadowGradient>
      </motion.div>

      {showOverlay &&
        createPortal(
          <AnimatePresence>
            <motion.div
              className="referral-tracker-overlay-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleCloseDetails}
              role="button"
              tabIndex={0}
              aria-label="Close overlay"
              onKeyDown={(e) => e.key === 'Escape' && handleCloseDetails()}
            >
              <motion.div
                className="referral-tracker-overlay-panel"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Card.NoShadowGradient className="referral-tracker-overlay-card">
                  {detailsLoading ? (
                    <div className="referral-tracker-loading referral-tracker-overlay-loading">
                      <div className="referral-tracker-spinner" />
                      <p className={textStyle(p, 'text-gray-600 mt-4')}>Loading details...</p>
                    </div>
                  ) : (
                    <ReferralDetails influencer={selectedInfluencer} onClose={handleCloseDetails} showActionButtons={false} />
                  )}
                </Card.NoShadowGradient>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default InfluencerReferralDashboard;
